<template>
  <!--
    Demanda urgente (D38) = médico PRÉ-ALOCADO: plantões afetados, empresa, o que falta,
    contagem regressiva até o plantão mais próximo e os 3 tempos do D29. Arrastável; quem decide
    se o movimento vale é o backend (D43: nunca aceita "Resolvido" com pendência).
  -->
  <article
    class="flex flex-col gap-2 rounded-lg border bg-white p-2.5 text-left shadow-sm"
    :class="[
      critical ? 'border-red-300' : 'border-[#e8e8ed]',
      canDrag ? 'cursor-grab active:cursor-grabbing' : ''
    ]"
    :draggable="canDrag"
    :aria-busy="isMoving ? 'true' : 'false'"
    :data-testid="`pre-allocation-card-${card.id}`"
    @dragstart="onDragStart"
  >
    <div class="flex items-start justify-between gap-2">
      <div class="min-w-0">
        <p class="text-[13px] font-bold text-textPrimary truncate">
          {{ card.professional.name }}
        </p>
        <p class="text-[11px] text-[#52526f] truncate">
          <template v-if="card.professional.crm">CRM {{ card.professional.crm }} · </template
          >{{ card.company.name }}
        </p>
      </div>
      <span
        v-if="timer"
        class="shrink-0 rounded px-1.5 py-0.5 text-[11px] font-bold tabular-nums"
        :class="TIMER_CLASS[timer.tone]"
        :title="
          critical ? 'Menos de 24h para o plantão mais próximo' : 'Tempo até o plantão mais próximo'
        "
        :data-testid="`pre-allocation-card-${card.id}-countdown`"
      >
        {{ timer.label }}
      </span>
      <LabelBadge v-else label="Definitiva" color="green" size="small" :icon="mdiCheckCircle" />
    </div>

    <ul class="flex flex-col gap-0.5">
      <li
        v-for="station in stations"
        :key="station.doctorStationId"
        class="flex items-center gap-1 text-[11px] text-textPrimary"
      >
        <svg viewBox="0 0 24 24" class="w-3 h-3 shrink-0 text-[#7f7f8e]" aria-hidden="true">
          <path :d="mdiCalendarClock" fill="currentColor" />
        </svg>
        <span class="truncate">
          {{ formatStationStart(station.start) }} · {{ station.scheduleName }}
        </span>
      </li>
    </ul>

    <ul v-if="card.missing.length" class="flex flex-col gap-1">
      <li v-for="item in card.missing" :key="`${item.field}-${item.key}`">
        <div class="flex items-center justify-between gap-2">
          <span class="text-[11px] text-textPrimary truncate">{{ item.name }}</span>
          <LabelBadge
            :label="DOCUMENT_STATUS_LABEL[statusOf(item)]"
            :color="statusOf(item) === ProfessionalDocumentStatus.IN_REVIEW ? 'yellow' : 'red'"
            size="small"
            :icon="null"
          />
        </div>
        <p v-if="pendingDetail(item)" class="text-[10px] text-negative leading-3">
          {{ pendingDetail(item) }}
        </p>
      </li>
    </ul>

    <dl
      class="grid grid-cols-[1fr_auto] gap-x-2 gap-y-0.5 rounded bg-[#f7f7fa] px-2 py-1.5 text-[10.5px]"
    >
      <dt class="text-[#7f7f8e]">Médico completar doc.</dt>
      <dd class="text-right font-semibold text-textPrimary tabular-nums">{{ times.completion }}</dd>
      <dt class="text-[#7f7f8e]">Envio → decisão</dt>
      <dd class="text-right font-semibold text-textPrimary tabular-nums">{{ times.decision }}</dd>
      <dt class="text-[#7f7f8e]">Espera pelo OK</dt>
      <dd
        class="text-right font-semibold tabular-nums"
        :class="resolved ? 'text-sucess' : 'text-textPrimary'"
      >
        {{ times.waiting }}
      </dd>
    </dl>

    <button
      v-if="!resolved"
      type="button"
      class="self-start text-[11px] font-medium text-primary30 underline disabled:opacity-60"
      :disabled="isMoving"
      :data-testid="`pre-allocation-card-${card.id}-resolve`"
      @click="emit('tryResolve', card)"
    >
      Marcar como resolvido
    </button>
  </article>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { mdiCalendarClock, mdiCheckCircle } from '@mdi/js'

import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'
import { DOCUMENT_STATUS_LABEL, pendingReasonText } from '@/components/ProfessionalDocuments/labels'
import { ProfessionalDocumentStatus } from '@/types/professional-documents.types'
import { PreAllocationColumn } from '@/types/qualification-eligibility.types'
import type { EligibilityReason, PreAllocationCard } from '@/types/qualification-eligibility.types'

import { countdown, formatStationStart, preAllocationTimeLabels, stationsByStart } from '../model'
import type { CountdownTone } from '../model'

const props = withDefaults(defineProps<{ card: PreAllocationCard; isMoving?: boolean }>(), {
  isMoving: false
})
const emit = defineEmits<{
  tryResolve: [card: PreAllocationCard]
  dragStart: [card: PreAllocationCard]
}>()

const TIMER_CLASS: Record<CountdownTone, string> = {
  crit: 'bg-red-50 text-red-700',
  warn: 'bg-orange-50 text-orange-700',
  neutral: 'bg-[#f0f0f4] text-[#52526f]'
}

const resolved = computed(() => props.card.column === PreAllocationColumn.RESOLVED)
const canDrag = computed(() => !resolved.value && !props.isMoving)
const timer = computed(() => countdown(props.card))
const critical = computed(() => timer.value?.tone === 'crit')
const stations = computed(() => stationsByStart(props.card))
const times = computed(() => preAllocationTimeLabels(props.card))

/** Motivo de selo sem documento chega sem status: conta como Pendente (A4 §5). */
const statusOf = (item: EligibilityReason): ProfessionalDocumentStatus =>
  item.status ?? ProfessionalDocumentStatus.PENDING

const pendingDetail = (item: EligibilityReason): string =>
  pendingReasonText({
    status: statusOf(item),
    pendingReason: item.pendingReason,
    expiresAt: item.expiresAt
  })

function onDragStart(event: DragEvent) {
  // Firefox só inicia o arraste com algum dado; quem lê o cartão é o Kanban (evento abaixo).
  event.dataTransfer?.setData('text/plain', String(props.card.id))
  emit('dragStart', props.card)
}
</script>
